import React, { useEffect, createElement } from 'react';
import { SocialProof } from './components/SocialProof';
import { PainPoints } from './components/PainPoints';
import { ProductShowcase } from './components/ProductShowcase';
import { WhatYouGet } from './components/WhatYouGet';
import { Bonuses } from './components/Bonuses';
import { Countdown } from './components/Countdown';
import { Pricing } from './components/Pricing';
import { ProBusiness } from './components/ProBusiness';
import { Guarantee } from './components/Guarantee';
import { Testimonials } from './components/Testimonials';
import { FAQ } from './components/FAQ';
import { DarkCTA } from './components/DarkCTA';
import { Footer } from './components/Footer';
import { PurchaseNotifications } from './components/PurchaseNotifications';
// Optimized hero image with Cloudinary transforms
const HERO_IMG = 'https://res.cloudinary.com/dowzlbqlz/image/upload/v1777816369/hf_20260503_134623_1bb422cd-84e7-4ec9-9a58-4fe2f3159cdc-Photoroom_d73vml.webp';
const OG_IMG = 'https://cdn.magicpatterns.com/uploads/9uMhQHg3RXXbKPqANDF9n5/hf_20260423_164447_343907b5-95aa-49ff-ba71-eeea9d0be6f6-Photoroom.webp';
const TAGS = ['📄 100% PDF', '⚡ Acceso Inmediato', '🇦🇷 Argentina', '🎓 Toda la Carrera', '✅ Sistema Listo', '📚 6 Tomos + 6 Bonos'];
export function App() {
  // SEO meta tags — deferred to idle time
  useEffect(() => {
    const runSeo = () => {
      document.title = 'Megapack Kinesiología: Carrera Resuelta — El Sistema Completo para Estudiantes de Kinesiología en Argentina';
      document.documentElement.lang = 'es';
      const setMeta = (name: string, content: string, property?: boolean) => {
        const attr = property ? 'property' : 'name';
        let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute(attr, name);
          document.head.appendChild(el);
        }
        el.content = content;
      };
      const description = 'El sistema completo de estudio para estudiantes de Kinesiología en Argentina. 6 Tomos de 60 páginas cada uno con todo el contenido de la carrera organizado, más 6 bonos exclusivos: Guía de Supervivencia Hospitalaria, Fichas de Evaluación, Mnemotecnias de Anatomía, Signos Vitales y Alarmas, Guía de Finales y Cómo Empezar a Trabajar. Acceso inmediato.';
      const url = 'https://kinesiologiacarreraresuelta.com';
      const image = OG_IMG;
      setMeta('description', description);
      setMeta('keywords', 'megapack kinesiología, carrera resuelta, estudiar kinesiología argentina, material kinesiología universitario, biomecánica argentina, morfofisiología kinesiología, casos clínicos kinesiología, guía práctica hospitalaria, técnicas manuales argentina, apuntes kinesiología UBA UNLP UNC');
      setMeta('robots', 'index, follow');
      setMeta('author', 'Megapack Kinesiología: Carrera Resuelta');
      setMeta('viewport', 'width=device-width, initial-scale=1');
      if (!document.querySelector('link[rel="canonical"]')) {
        const canonical = document.createElement('link');
        canonical.rel = 'canonical';
        canonical.href = url;
        document.head.appendChild(canonical);
      }
      setMeta('og:title', 'Megapack Kinesiología: Carrera Resuelta — El Sistema Completo para Estudiantes de Kinesiología en Argentina', true);
      setMeta('og:description', description, true);
      setMeta('og:image', image, true);
      setMeta('og:url', url, true);
      setMeta('og:type', 'product', true);
      setMeta('og:locale', 'es_AR', true);
      setMeta('og:site_name', 'Megapack Kinesiología: Carrera Resuelta™', true);
      setMeta('twitter:card', 'summary_large_image');
      setMeta('twitter:title', 'Megapack Kinesiología: Carrera Resuelta — El Sistema Completo para Estudiantes de Kinesiología');
      setMeta('twitter:description', description);
      setMeta('twitter:image', image);
      if (!document.querySelector('script[data-ld="product"]')) {
        const ld = document.createElement('script');
        ld.type = 'application/ld+json';
        ld.setAttribute('data-ld', 'product');
        ld.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'Megapack Kinesiología: Carrera Resuelta™',
          description,
          image,
          brand: {
            '@type': 'Brand',
            name: 'Megapack Kinesiología: Carrera Resuelta™'
          },
          offers: {
            '@type': 'AggregateOffer',
            lowPrice: '19990',
            highPrice: '27990',
            priceCurrency: 'ARS',
            availability: 'https://schema.org/InStock',
            offerCount: 2
          },
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.9',
            reviewCount: '512'
          }
        });
        document.head.appendChild(ld);
      }
      if (!document.querySelector('script[data-ld="org"]')) {
        const orgLd = document.createElement('script');
        orgLd.type = 'application/ld+json';
        orgLd.setAttribute('data-ld', 'org');
        orgLd.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Megapack Kinesiología: Carrera Resuelta™',
          url,
          logo: image,
          email: 'hola@kinesiologiacarreraresuelta.com',
          sameAs: ['https://www.instagram.com/kinesiologiacarreraresuelta']
        });
        document.head.appendChild(orgLd);
      }
      if (!document.querySelector('script[data-ld="faq"]')) {
        const faqLd = document.createElement('script');
        faqLd.type = 'application/ld+json';
        faqLd.setAttribute('data-ld', 'faq');
        faqLd.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [{
            '@type': 'Question',
            name: '¿Qué incluye el Megapack Kinesiología: Carrera Resuelta?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '6 Tomos de 60 páginas cada uno con todo el contenido de la carrera de Kinesiología organizado por área, más 6 bonos exclusivos: Guía de Supervivencia Hospitalaria, Fichas de Evaluación Kinésica, Mnemotecnias de Anatomía, Signos Vitales y Valores de Alarma, Guía de Finales y Cómo Empezar a Trabajar como Kinesiólogo. Más el Máster Kinesiología disponible como upsell.'
            }
          }, {
            '@type': 'Question',
            name: '¿Cómo recibo el material?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Todo es 100% digital en formato PDF. Recibís acceso inmediato en tu email dentro de los 5 minutos posteriores a la compra.'
            }
          }, {
            '@type': 'Question',
            name: '¿Tiene garantía?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Sí, ofrecemos garantía de 7 días sin preguntas. Si no te sirve, te devolvemos el 100% de tu dinero.'
            }
          }, {
            '@type': 'Question',
            name: '¿Está adaptado a Argentina?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Sí, 100%. Todo el contenido está adaptado al plan de estudios argentino, con terminología, escalas y protocolos utilizados en las universidades nacionales.'
            }
          }, {
            '@type': 'Question',
            name: '¿Para qué año de la carrera está pensado?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Para toda la carrera. El Tomo 1 cubre Morfofisiología, los Tomos 2 al 5 cubren el núcleo clínico y el Tomo 6 cubre la práctica hospitalaria y la salida laboral.'
            }
          }]
        });
        document.head.appendChild(faqLd);
      }
    };
    if ('requestIdleCallback' in window) {
      ;
      (window as any).requestIdleCallback(runSeo, {
        timeout: 1500
      });
    } else {
      setTimeout(runSeo, 100);
    }
  }, []);
  // Facebook Pixel — loads immediately as per Meta standard
  useEffect(() => {
    if (typeof window !== 'undefined' && !(window as any).fbq) {
      const f = window as any;
      const n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      (window as any).fbq('init', '1215304437119773');
      (window as any).fbq('track', 'PageView');
      const t = document.createElement('script');
      t.async = true;
      t.src = 'https://connect.facebook.net/en_US/fbevents.js';
      const s = document.getElementsByTagName('script')[0];
      s?.parentNode?.insertBefore(t, s);
    }
  }, []);
  return <>
      <noscript data-id="element-0">
        <img height="1" width="1" style={{
        display: 'none'
      }} src="https://www.facebook.com/tr?id=1215304437119773&ev=PageView&noscript=1" alt="" data-id="element-1" />
      </noscript>

      <header className="relative flex w-full flex-col items-center justify-center text-center text-gray-900 text-base leading-6 px-5 sm:px-6 py-12 sm:py-16" role="banner" style={{
      backgroundImage: 'linear-gradient(180deg, rgb(219,234,254) 0%, rgb(239,246,255) 50%, rgb(219,234,254) 100%)',
      backgroundColor: 'rgb(219,234,254)'
    }} data-id="element-2">
        {/* Badge */}
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-blue-400/30 bg-white/80 px-4 py-1.5 shadow-sm" data-id="element-3">
          <span className="text-xs sm:text-sm font-bold text-blue-700 tracking-wide uppercase" data-id="element-4">
            🎓 Sistema de Estudio para Kinesiología · Estudiantes Argentina
          </span>
        </div>

        {/* H1 */}
        <h1 className="sm:text-5xl md:text-[72px] sm:leading-snug md:leading-[88px] font-black max-w-4xl text-gray-900 m-0 mb-2 tracking-tight pt-[10px] pb-[10px] text-[23px]" data-id="element-5">
          Toda la carrera de Kinesiología
          <br data-id="element-6" />
          en un solo lugar.
          <br data-id="element-7" />
          <span className="text-blue-600 mt-1 inline-block" data-id="element-8">
            Sin buscar más, sin improvisar.
          </span>
        </h1>

        {/* Mockup (LCP element) */}
        <div className="my-6 sm:my-8 w-full max-w-[320px] sm:max-w-[380px] aspect-square relative" data-id="element-9">
          <div className="absolute inset-4 rounded-full z-0 pointer-events-none" style={{
          background: 'radial-gradient(circle, rgba(59,130,246,0.30) 0%, rgba(37,99,235,0.10) 50%, transparent 70%)'
        }} data-id="element-10" />
          <div className="relative z-10 w-full h-full" data-id="element-11">
            <img src={HERO_IMG} alt="Megapack Kinesiología: Carrera Resuelta — Sistema Completo para Estudiantes de Kinesiología Argentina" width="400" height="400" fetchPriority="high" decoding="sync" className="block max-w-full w-full h-auto object-contain" data-id="element-12" />
          </div>
        </div>

        {/* Subtítulo */}
        <p className="max-w-xl text-base sm:text-lg font-semibold text-gray-700 leading-7 m-0 mb-4" data-id="element-13">
          El sistema que los mejores estudiantes de Kinesiología usan para
          aprobar materias, dominar la clínica, sobrevivir las prácticas
          hospitalarias y terminar la carrera{' '}
          <span className="text-gray-900 font-black" data-id="element-14">
            sin depender de apuntes incompletos ni de la suerte en los finales.
          </span>
        </p>

        {/* Tagline */}
        <p className="max-w-xl text-lg sm:text-xl font-black text-blue-600 m-0 mb-6" data-id="element-15">
          "360 páginas de Kinesiología organizadas, listas y aplicables. Del
          primer parcial al título."
        </p>

        {/* Tags */}
        <div className="mb-7 flex max-w-lg flex-wrap items-center justify-center gap-2" data-id="element-16">
          {TAGS.map(tag => <span key={tag} className="whitespace-nowrap bg-white/80 text-xs sm:text-sm font-semibold text-gray-900 leading-5 border border-blue-400/30 rounded-full px-3 py-1 shadow-sm" data-id="element-17">
              {tag}
            </span>)}
        </div>

        {/* CTA Button */}
        <a href="#pricing" onClick={() => {
        if (typeof window !== 'undefined' && (window as any).fbq) {
          ;
          (window as any).fbq('track', 'Lead', {
            content_name: 'Hero CTA Click',
            content_category: 'Kinesiología Carrera Resuelta'
          });
        }
      }} className="block w-full max-w-sm text-white text-lg sm:text-xl font-black rounded-xl px-6 sm:px-8 py-4 mb-4 no-underline text-center shadow-lg hover:opacity-90 transition-opacity" style={{
        background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 50%, #2563eb 100%)'
      }} data-id="element-18">
          🎓 → QUIERO MI MEGAPACK AHORA
        </a>

        {/* Trust Badges */}
        <div className="flex flex-row flex-wrap items-center justify-center gap-3 sm:gap-4 m-0" data-id="element-19">
          <span className="inline-flex items-center gap-1.5 bg-white/80 border border-gray-200 rounded-full px-3 py-1.5 text-xs sm:text-sm text-gray-600 shadow-sm" data-id="element-20">
            🛡️ Garantía 7 días
          </span>
          <span className="inline-flex items-center gap-1.5 bg-white/80 border border-gray-200 rounded-full px-3 py-1.5 text-xs sm:text-sm text-gray-600 shadow-sm" data-id="element-21">
            🔒 Pago seguro
          </span>
          <span className="inline-flex items-center gap-1.5 bg-white/80 border border-gray-200 rounded-full px-3 py-1.5 text-xs sm:text-sm text-gray-600 shadow-sm" data-id="element-22">
            💳 Mercado Pago | Tarjeta | Transferencia
          </span>
        </div>
      </header>

      <main data-id="element-23">
        <SocialProof data-id="element-24" />
        <PainPoints data-id="element-25" />
        <ProductShowcase data-id="element-26" />
        <WhatYouGet data-id="element-27" />
        <Bonuses data-id="element-28" />
        <Countdown data-id="element-29" />
        <Pricing data-id="element-30" />
        <ProBusiness data-id="element-31" />
        <Guarantee data-id="element-32" />
        <Testimonials data-id="element-33" />
        <FAQ data-id="element-34" />
        <DarkCTA data-id="element-35" />
      </main>

      <Footer data-id="element-36" />
      <PurchaseNotifications data-id="element-37" />
    </>;
}